// The telephone scale bar (R1801, card 05 of mockup round 086,
// ui-map-interface.md §12.0).
//
// The scale bar of the cartouche stands on a desktop only, so a telephone
// had none (review 2026-10-08 §2.5). This bar stands next to the round
// minimap on a telephone: at most `scale_bar_px` wide, in whole cells of
// the map, and in the face of the chart reading. It reuses the cartouche's
// count rule (`fitScaleCells`), so the two bars never disagree on how a
// count is chosen.
//
// The bar is DOM inside `#ui`, so its width is in chrome px: a cell of
// `COL_PITCH` map px is `COL_PITCH × zoom` screen px, which is that figure
// divided by the UI scale in chrome px (R1556). The frame loop calls
// `update` every frame, and a frame that changed nothing writes nothing
// (§10.3).

import { COL_PITCH } from '../gen/balance';
import { LAYOUT_SCALE_BAR_CELLS, LAYOUT_SCALE_BAR_PX } from '../gen/presentation';
import { viewpanel as S } from '../gen/strings';
import { fitScaleCells } from './chart/cartouche';

/**
 * How many whole cells the bar shows at a zoom: the most cells, up to
 * `scale_bar_cells`, whose bar is no wider than `scale_bar_px` chrome px.
 * `ui` is the UI scale: the budget is chrome px and the cells are screen
 * px. One cell is the floor, so a deep zoom still shows one cell.
 */
export function scaleBarCells(zoom: number, ui = 1): number {
  return fitScaleCells(LAYOUT_SCALE_BAR_PX * ui, zoom, LAYOUT_SCALE_BAR_CELLS, 1);
}

/** The bar's width in chrome px for `cells` cells at a zoom and a UI scale. */
export function scaleBarPx(cells: number, zoom: number, ui = 1): number {
  return Math.round((cells * COL_PITCH * zoom) / (ui > 0 ? ui : 1));
}

export class ScaleBar {
  /** The element; the overlay puts it on `#ui`. */
  readonly el: HTMLElement;
  private readonly bar: HTMLElement;
  private readonly len: HTMLElement;
  private shown = false;
  private cells = -1;
  private px = -1;
  private hist = false;

  constructor() {
    const el = document.createElement('div');
    el.id = 'scalebar';
    el.setAttribute('aria-hidden', 'true');
    el.innerHTML = '<span class="sb-zero">0</span><span class="sb-bar"></span><span class="sb-len"></span>';
    this.el = el;
    this.bar = el.querySelector('.sb-bar')!;
    this.len = el.querySelector('.sb-len')!;
  }

  /**
   * One frame. `shown` is false off a telephone, under the chart setting
   * `off` and while the round unit is not on the glass.
   */
  update(zoom: number, historical: boolean, shown: boolean, ui = 1): void {
    if (shown !== this.shown) {
      this.shown = shown;
      this.el.classList.toggle('on', shown);
    }
    if (!shown) return;
    if (historical !== this.hist) {
      this.hist = historical;
      this.el.classList.toggle('historical', historical);
    }
    const cells = scaleBarCells(zoom, ui);
    if (cells !== this.cells) {
      this.cells = cells;
      this.bar.innerHTML = '<i></i>'.repeat(cells);
      this.len.textContent = S.scale_cells({ n: cells });
    }
    const px = scaleBarPx(cells, zoom, ui);
    if (px !== this.px) {
      this.px = px;
      this.bar.style.width = `${px}px`;
    }
  }
}
